
import { useState, useRef, useEffect } from "react";
import PaystackCheckout from "./PaystackCheckout";
import PaymentSuccess from "./PaymentSuccess";

const COUNTRIES = [
  "Zimbabwe","South Africa","Zambia","Kenya","Nigeria","Ghana","Tanzania",
  "Uganda","Botswana","Namibia","Mozambique","Malawi","Rwanda","Ethiopia",
  "Senegal","Cameroon","Angola","Lesotho","Eswatini","Mauritius",
  "Côte d'Ivoire","Egypt","Morocco","Tunisia","Other"
];
const GRADES = [
  "Form 1 / Grade 8","Form 2 / Grade 9","Form 3 / Grade 10",
  "Form 4 / Grade 11","Form 5 / Grade 12",  "Form 6 / A-Level"
];
const PHASES = ["Warm Up","Energy Mapping","School Lens","Values","World Expansion","Anchoring"];
const ADMIN_PASS = "careergps2025";
const STATUS_COLORS = {"New Lead":"#ef4444","Contacted":"#f59e0b","Converted":"#16a34a","Not Interested":"#6b7280"};
const STATUSES = ["New Lead","Contacted","Converted","Not Interested"];

 const COUNSELOR_SYS = `You are a fun, friendly career guide for African high school students at Career GPS Africa. You talk like a cool older brother or sister — simple, warm, encouraging. Use Grade 5 English only: short sentences, easy words, no big vocabulary. Never sound like a teacher or a formal counsellor.

Guide the student through 5 phases of chatting:

PHASE 1 - WARM UP (2-3 exchanges): Keep it super light and fun. Tell them this is not a test. Frame it like a treasure hunt to find clues about who they are.
PHASE 2 - ENERGY MAPPING (4-5 exchanges): Find out what they love doing. Listen for clues — are they a Builder, Helper, Explainer, Organiser, Performer, Investigator, or Creator? Ask about times they forgot to eat because they were so busy doing something fun.
PHASE 3 - SCHOOL LENS (2-3 exchanges): Ask about subjects in a careful way. NOT which subject they are best at — but which subject feels most like THEM. Ask things like "Which subject feels most natural to you, even if it is not your top mark?" and "Is there a topic at school you wish they taught more of, or something you wish was a subject?" Listen for the difference between performance and genuine interest.
PHASE 4 - VALUES (3-4 exchanges): Ask simple either/or situational questions. Do they like working with people or alone? Do they like making things you can touch or ideas in your head? Same thing every day or something new every day?
PHASE 5 - WORLD EXPANSION (2-3 exchanges): Show them 4-5 jobs they have probably NEVER heard of that match what they told you. Use examples from Africa and their own country. Make it feel exciting — like wow, that is a real job!
PHASE 6 - WRAP UP (1-2 exchanges): Tell them what you found out about them. Make them feel proud and excited about the future.

RULES:
- Use ONLY simple Grade 5 English. Short words. Short sentences. Easy to read.
- Max 2-3 sentences per response + EXACTLY ONE question. Never ask two questions. Pick one and drop the rest.
- Sound like a fun friend, not a teacher or counsellor.
- Never only suggest doctor/lawyer/engineer/teacher/accountant — always go deeper and wider.
- Bring in African examples naturally — their country, African cities, African companies.
- After all 5 phases are done (~14-20 exchanges total), ask the student: "I think I have everything I need to build your Career Destination Report! 🎉 Should I go ahead and create it now, or do you want to keep chatting a bit more?" End that message with exactly: [ASK_READY]
- Do NOT use [ASK_READY] until all 5 phases are genuinely done.
- If the student says yes/ready/go ahead/generate — respond with one short excited sentence then end with exactly: [SESSION_COMPLETE]
- If the student says no/more/keep going — continue naturally for a few more exchanges then ask again.
- You already know the student's name, age, grade, school and country.`;

const SUMMARY_SYS = `Extract key career discovery insights from this chat transcript. Return ONLY a short JSON summary, raw, no markdown. Structure: {"name":"student name","energyPatterns":["pattern1","pattern2"],"strengths":["s1","s2","s3"],"values":["v1","v2"],"interests":["i1","i2","i3"],"avoids":"brief note","awareness":"brief note on career awareness level"}`;

const REPORT_SYS = `You generate Career Compass Reports for African high school students. Return ONLY raw valid JSON. No markdown, no backticks. Start with { and end with }.

Keep ALL string values under 25 words. Be concise.

Structure:
{"archetype":"The [Label]","archetypeDesc":"2 short sentences","strengths":[{"name":"S1","evidence":"brief"},{"name":"S2","evidence":"brief"},{"name":"S3","evidence":"brief"}],"values":"2 short sentences","careerFamilies":[{"name":"Family 1","fit":"1 sentence","careers":["C1","C2","C3","C4","C5"],"africa":"1 short sentence"},{"name":"Family 2","fit":"1 sentence","careers":["C1","C2","C3","C4","C5"],"africa":"1 short sentence"},{"name":"Family 3","fit":"1 sentence","careers":["C1","C2","C3","C4","C5"],"africa":"1 short sentence"}],"statement":"2 sentences starting with I am someone who","actions":[{"type":"Person to Interview","detail":"brief"},{"type":"Watch or Read","detail":"brief"},{"type":"Try This","detail":"brief"}],"score":7,"scoreLabel":"Emerging Explorer","scoreNote":"1 short encouraging sentence","counselorNote":"2 short sentences to student","parentNote":"3 short sentences to parent about findings and next step with Career GPS Africa"}`;

const iStyle = (err) => ({
  width:"100%",padding:"10px 12px",borderRadius:"8px",fontSize:"14px",
  border:`1.5px solid ${err?"#ef4444":"#e5e7eb"}`,outline:"none",
  boxSizing:"border-box",background:"white",color:"#111",fontFamily:"inherit"
});

function Field({label,error,span,children}) {
  return (
    <div style={{gridColumn:span===2?"1 / -1":"auto"}}>
      <label style={{display:"block",fontSize:"13px",fontWeight:"600",color:"#374151",marginBottom:"4px"}}>{label}</label>
      {children}
      {error&&<p style={{color:"#ef4444",fontSize:"12px",margin:"3px 0 0"}}>{error}</p>}
    </div>
  );
}

function PhaseDots({current}) {
  return (
    <div style={{display:"flex",gap:"5px",alignItems:"center"}}>
      {PHASES.map((_,i)=>(
        <div key={i} style={{width:i===current?"18px":"7px",height:"7px",borderRadius:"4px",
          background:i<=current?"#fbbf24":"rgba(255,255,255,0.3)",transition:"all 0.3s"}}/>
      ))}
    </div>
  );
}

function AdminLogin({onSuccess,onBack}) {
  const [pw,setPw]=useState("");
  const [err,setErr]=useState(false);
  const attempt=()=>{
    if(pw===ADMIN_PASS) onSuccess();
    else { setErr(true); setPw(""); }
  };
  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(150deg,#14532d,#166534)",
      display:"flex",alignItems:"center",justifyContent:"center",padding:"20px",fontFamily:"system-ui,sans-serif"}}>
      <div style={{background:"white",borderRadius:"20px",padding:"32px",width:"100%",maxWidth:"360px",
        boxShadow:"0 24px 64px rgba(0,0,0,0.35)"}}>
        <div style={{textAlign:"center",marginBottom:"24px"}}>
          <div style={{fontSize:"36px",marginBottom:"8px"}}>🔐</div>
          <h2 style={{margin:0,fontSize:"20px",fontWeight:"800",color:"#111"}}>Admin Access</h2>
          <p style={{margin:"6px 0 0",fontSize:"13px",color:"#6b7280"}}>Career GPS Africa Lead Dashboard</p>
        </div>
        <label style={{fontSize:"13px",fontWeight:"600",color:"#374151",display:"block",marginBottom:"6px"}}>Password</label>
        <input type="password" value={pw} onChange={e=>{setPw(e.target.value);setErr(false);}}
          onKeyDown={e=>e.key==="Enter"&&attempt()}
          placeholder="Enter admin password"
          style={{width:"100%",padding:"10px 12px",borderRadius:"8px",fontSize:"14px",boxSizing:"border-box",
            border:`1.5px solid ${err?"#ef4444":"#e5e7eb"}`,outline:"none",fontFamily:"inherit"}}/>
        {err&&<p style={{color:"#ef4444",fontSize:"12px",margin:"4px 0 0"}}>Incorrect password. Try again.</p>}
        <button onClick={attempt} style={{width:"100%",padding:"12px",marginTop:"16px",border:"none",
          borderRadius:"10px",fontSize:"15px",fontWeight:"700",color:"white",cursor:"pointer",
          background:"linear-gradient(135deg,#16a34a,#15803d)"}}>
          Enter Dashboard →
        </button>
        <button onClick={onBack} style={{width:"100%",padding:"10px",marginTop:"10px",border:"none",
          borderRadius:"10px",fontSize:"14px",color:"#6b7280",cursor:"pointer",background:"#f9fafb"}}>
          ← Back
        </button>
      </div>
    </div>
  );
}

function AdminScreen({onBack}) {
  const [leads,setLeads]=useState([]);
  const [loading,setLoading]=useState(true);
  const [selected,setSelected]=useState(null);

  useEffect(()=>{
 (async()=>{
      try {
        const res = await fetch('/api/getleads');
        const data = await res.json();
        const formatted = data.map(lead => ({
          id: lead.id,
          completedAt: lead.completed_at,
          student: {
            parentName: lead.parent_name,
            name: lead.student_name,
            age: lead.age,
            grade: lead.grade,
            school: lead.school,
            country: lead.country,
            email: lead.email,
            whatsapp: lead.whatsapp
          },
          parentName: lead.parent_name,
          archetype: lead.archetype,
          score: lead.score,
          scoreLabel: lead.score_label,
          careerFamilies: lead.career_families?.split(', '),
          careerFamiliesFull: lead.career_families_full ? JSON.parse(lead.career_families_full) : null,
          statement: lead.statement,
          parentNote: lead.parent_note,
          status: lead.status
        }));
        setLeads(formatted);
      } catch { setLeads([]); }
      setLoading(false);
    })();
  },[]);

  const updateStatus=async(lead,status)=>{
    const updated={...lead,status};
    console.log("Status updated:", lead.id, status);
    setLeads(l=>l.map(x=>x.id===lead.id?updated:x));
    if(selected?.id===lead.id) setSelected(updated);
  };

  const fmt=d=>new Date(d).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});

  return (
    <div style={{minHeight:"100vh",background:"#f9fafb",fontFamily:"system-ui,sans-serif"}}>
      <div style={{background:"linear-gradient(135deg,#14532d,#166534)",padding:"16px 20px",
        display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
          <span style={{fontSize:"22px"}}>🧭</span>
          <div>
            <div style={{color:"white",fontWeight:"800",fontSize:"15px"}}>Career GPS Africa</div>
            <div style={{color:"#86efac",fontSize:"11px"}}>Lead Dashboard</div>
          </div>
        </div>
        <button onClick={onBack} style={{background:"rgba(255,255,255,0.15)",border:"none",
          color:"white",padding:"6px 14px",borderRadius:"8px",cursor:"pointer",fontSize:"13px"}}>← Back</button>
      </div>

      <div style={{maxWidth:"700px",margin:"0 auto",padding:"20px 16px"}}>
        <div style={{display:"flex",gap:"12px",marginBottom:"20px",flexWrap:"wrap"}}>
          {[["Total Leads",leads.length,"#3b82f6"],
            ["New",leads.filter(l=>l.status==="New Lead").length,"#ef4444"],
            ["Contacted",leads.filter(l=>l.status==="Contacted").length,"#f59e0b"],
            ["Converted",leads.filter(l=>l.status==="Converted").length,"#16a34a"]
          ].map(([label,val,col])=>(
            <div key={label} style={{flex:"1 1 100px",background:"white",borderRadius:"12px",
              padding:"14px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)",borderTop:`3px solid ${col}`}}>
              <div style={{fontSize:"26px",fontWeight:"900",color:col}}>{val}</div>
              <div style={{fontSize:"12px",color:"#6b7280",fontWeight:"600"}}>{label}</div>
            </div>
          ))}
        </div>

        {loading&&<div style={{textAlign:"center",padding:"40px",color:"#6b7280"}}>Loading leads...</div>}
        {!loading&&leads.length===0&&(
          <div style={{textAlign:"center",padding:"40px",color:"#6b7280"}}>
            <div style={{fontSize:"40px",marginBottom:"12px"}}>📭</div>
            <p>No leads yet. Share the tool and they will start coming in!</p>
          </div>
        )}

        {leads.map(lead=>(
          <div key={lead.id} style={{background:"white",borderRadius:"14px",marginBottom:"12px",
            boxShadow:"0 2px 8px rgba(0,0,0,0.06)",overflow:"hidden",
            border:selected?.id===lead.id?"2px solid #16a34a":"2px solid transparent"}}>
            <div style={{padding:"14px 16px",cursor:"pointer"}} onClick={()=>setSelected(selected?.id===lead.id?null:lead)}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:"10px"}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:"700",fontSize:"15px",color:"#111"}}>{lead.student?.name}</div>
                  <div style={{fontSize:"12px",color:"#6b7280",marginTop:"2px"}}>
                    {lead.student?.grade} · {lead.student?.school} · {lead.student?.country}
                  </div>
                  <div style={{fontSize:"12px",color:"#374151",marginTop:"4px"}}>
                    {lead.archetype} · Score: <strong>{lead.score}/10</strong> · {lead.scoreLabel}
                  </div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:"11px",color:"#9ca3af",marginBottom:"6px"}}>{fmt(lead.completedAt)}</div>
                  <select value={lead.status} onClick={e=>e.stopPropagation()}
                    onChange={e=>updateStatus(lead,e.target.value)}
                    style={{fontSize:"11px",fontWeight:"700",border:"none",borderRadius:"6px",
                      padding:"4px 8px",cursor:"pointer",color:"white",
                      background:STATUS_COLORS[lead.status]||"#6b7280"}}>
                    {STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
            {selected?.id===lead.id&&(
              <div style={{borderTop:"1px solid #f3f4f6",padding:"14px 16px",background:"#f9fafb"}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px",marginBottom:"12px"}}>
                  <div style={{background:"white",borderRadius:"8px",padding:"10px"}}>
                    <div style={{fontSize:"11px",color:"#9ca3af",fontWeight:"600"}}>EMAIL</div>
                    <div style={{fontSize:"13px",color:"#111",fontWeight:"600",wordBreak:"break-all"}}>{lead.student?.email}</div>
                  </div>
                  <div style={{background:"white",borderRadius:"8px",padding:"10px"}}>
                    <div style={{fontSize:"11px",color:"#9ca3af",fontWeight:"600"}}>WHATSAPP</div>
                    <div style={{fontSize:"13px",color:"#111",fontWeight:"600"}}>{lead.student?.whatsapp}</div>
                  </div>
                </div>
                <div style={{background:"white",borderRadius:"8px",padding:"10px",marginBottom:"8px"}}>
                  <div style={{fontSize:"11px",color:"#9ca3af",fontWeight:"600",marginBottom:"4px"}}>CAREER FAMILIES</div>
                  <div style={{display:"flex",gap:"6px",flexWrap:"wrap"}}>
                   {lead.careerFamiliesFull ? lead.careerFamiliesFull.map((fam,i)=>(
  <div key={i} style={{marginBottom:"8px"}}>
    <div style={{fontWeight:"700",fontSize:"12px",color:"#15803d",marginBottom:"3px"}}>{fam.name}</div>
    <div style={{display:"flex",flexWrap:"wrap",gap:"4px"}}>
      {fam.careers?.map((c,j)=>(
        <span key={j} style={{background:"#f0fdf4",border:"1px solid #bbf7d0",
          borderRadius:"6px",padding:"2px 8px",fontSize:"11px",color:"#15803d"}}>{c}</span>
      ))}
    </div>
  </div>
)) : lead.careerFamilies?.map((f,i)=>(
  <span key={i} style={{background:"#f0fdf4",border:"1px solid #bbf7d0",
    borderRadius:"6px",padding:"3px 10px",fontSize:"12px",color:"#15803d",fontWeight:"600"}}>{f}</span>
))}
                  </div>
                </div>
                <div style={{background:"#fef3c7",borderRadius:"8px",padding:"10px",marginBottom:"10px"}}>
                  <div style={{fontSize:"11px",color:"#92400e",fontWeight:"600",marginBottom:"4px"}}>PARENT NOTE (use for follow-up call)</div>
                  <div style={{fontSize:"12px",color:"#78350f",lineHeight:"1.5"}}>{lead.parentNote}</div>
                </div>
                <div style={{display:"flex",gap:"8px"}}>
                  <a href={`https://wa.me/${lead.student?.whatsapp?.replace(/\D/g,"")}`} target="_blank"
                    style={{flex:1,padding:"10px",background:"#16a34a",color:"white",borderRadius:"8px",
                      textAlign:"center",fontSize:"13px",fontWeight:"700",textDecoration:"none",display:"block"}}>
                    💬 WhatsApp
                  </a>
                  <a href={`mailto:${lead.student?.email}`}
                    style={{flex:1,padding:"10px",background:"#3b82f6",color:"white",borderRadius:"8px",
                      textAlign:"center",fontSize:"13px",fontWeight:"700",textDecoration:"none",display:"block"}}>
                    ✉️ Email
                  </a>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function LandingScreen({student,setStudent,errors,onStart,loading,onAdmin}) {
  const f=(k,v)=>setStudent(s=>({...s,[k]:v}));
  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(150deg,#14532d 0%,#166534 60%,#15803d 100%)",
      display:"flex",alignItems:"center",justifyContent:"center",padding:"20px",fontFamily:"system-ui,sans-serif"}}>
      <div style={{width:"100%",maxWidth:"460px"}}>
        <div style={{textAlign:"center",marginBottom:"28px"}}>
          <div style={{display:"inline-flex",alignItems:"center",gap:"10px",marginBottom:"14px"}}>
            <div style={{width:"48px",height:"48px",background:"#f59e0b",borderRadius:"14px",
              display:"flex",alignItems:"center",justifyContent:"center",fontSize:"24px",boxShadow:"0 4px 12px rgba(245,158,11,0.4)"}}>🧭</div>
            <div style={{textAlign:"left"}}>
              <div style={{color:"white",fontWeight:"800",fontSize:"18px",letterSpacing:"-0.5px"}}>Career GPS Africa</div>
              <div style={{color:"#86efac",fontSize:"11px",fontWeight:"500"}}>Personalised Career Discovery</div>
            </div>
          </div>
          <h1 style={{color:"white",fontSize:"26px",fontWeight:"800",margin:"0 0 10px",lineHeight:"1.25"}}>
            What Career Is Your<br/>Child <span style={{color:"#fbbf24"}}>Built For?</span>
          </h1>
          <p style={{color:"#bbf7d0",fontSize:"14px",margin:0,lineHeight:"1.6"}}>
            A <span style={{color:"#fbbf24",fontWeight:"700"}}>free</span> 20-minute personalised career discovery session — your child gets a Career Compass Report at the end.
          </p>
        </div>

        <div style={{background:"white",borderRadius:"20px",padding:"26px",boxShadow:"0 24px 64px rgba(0,0,0,0.35)"}}>
          <p style={{margin:"0 0 18px",color:"#374151",fontWeight:"700",fontSize:"15px"}}>Tell us about the student</p>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px"}}>
            <Field label="Parent / Guardian Name" error={errors.parentName} span={2}>
              <input placeholder="e.g. Chipo Moyo" value={student.parentName} onChange={e=>f("parentName",e.target.value)} style={iStyle(errors.parentName)}/>
            </Field>
            <Field label="Student Full Name" error={errors.name} span={2}>
              <input placeholder="e.g. Tanaka Moyo" value={student.name} onChange={e=>f("name",e.target.value)} style={iStyle(errors.name)}/>
            </Field>
            <Field label="Age" error={errors.age}>
              <input type="number" placeholder="14" min="12" max="20" value={student.age} onChange={e=>f("age",e.target.value)} style={iStyle(errors.age)}/>
            </Field>
            <Field label="Country" error={errors.country}>
              <select value={student.country} onChange={e=>f("country",e.target.value)} style={iStyle(errors.country)}>
                <option value="">Select...</option>
                {COUNTRIES.map(c=><option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="School Name" error={errors.school} span={2}>
              <input placeholder="e.g. Harare High School" value={student.school} onChange={e=>f("school",e.target.value)} style={iStyle(errors.school)}/>
            </Field>
            <Field label="Current Grade / Form" error={errors.grade} span={2}>
              <select value={student.grade} onChange={e=>f("grade",e.target.value)} style={iStyle(errors.grade)}>
                <option value="">Select grade...</option>
                {GRADES.map(g=><option key={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Parent Email" error={errors.email} span={2}>
              <input type="email" placeholder="parent@email.com" value={student.email} onChange={e=>f("email",e.target.value)} style={iStyle(errors.email)}/>
            </Field>
            <Field label="WhatsApp Number" error={errors.whatsapp} span={2}>
              <input placeholder="+263 77 123 4567" value={student.whatsapp} onChange={e=>f("whatsapp",e.target.value)} style={iStyle(errors.whatsapp)}/>
            </Field>
          </div>
          <button onClick={onStart} disabled={loading} style={{
            width:"100%",padding:"14px",marginTop:"16px",border:"none",borderRadius:"12px",fontSize:"16px",
            fontWeight:"700",color:"white",cursor:loading?"not-allowed":"pointer",
            background:loading?"#9ca3af":"linear-gradient(135deg,#16a34a,#15803d)",
            boxShadow:loading?"none":"0 4px 16px rgba(22,163,74,0.35)"}}>
            {loading?"Starting your session...":"Start Free Career Discovery →"}
          </button>
          <p style={{textAlign:"center",fontSize:"12px",color:"#9ca3af",margin:"10px 0 0"}}>
            🔒 Private & secure. Takes about 20 minutes.
          </p>
          <p style={{textAlign:"center",margin:"10px 0 0"}}>
            <span onClick={onAdmin} style={{fontSize:"11px",color:"#d1d5db",cursor:"pointer",textDecoration:"underline"}}>Admin access</span>
          </p>
        </div>

        <div style={{display:"flex",justifyContent:"center",gap:"24px",marginTop:"20px"}}>
          {["🌍 Pan-African","✨ Personalised","📄 Free Report"].map(t=>(
            <div key={t} style={{color:"#86efac",fontSize:"12px",fontWeight:"600"}}>{t}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChatScreen({messages,input,setInput,onSend,isTyping,student,messagesEndRef,exchangeCount,awaitingConfirm,onConfirmYes,onConfirmNo}) {
  const phaseIdx=Math.min(Math.floor(exchangeCount/3),4);
  return (
    <div style={{height:"100vh",display:"flex",flexDirection:"column",background:"#f0fdf4",fontFamily:"system-ui,sans-serif"}}>
      <div style={{background:"linear-gradient(135deg,#14532d,#166534)",padding:"12px 16px",
        display:"flex",alignItems:"center",gap:"12px",flexShrink:0}}>
        <div style={{width:"40px",height:"40px",background:"#f59e0b",borderRadius:"50%",
          display:"flex",alignItems:"center",justifyContent:"center",fontSize:"18px",flexShrink:0}}>🧭</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{color:"white",fontWeight:"700",fontSize:"15px"}}>Career GPS Africa</div>
          <div style={{color:"#86efac",fontSize:"12px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
            {student.name}'s Career Discovery
          </div>
        </div>
        <div style={{textAlign:"right",flexShrink:0}}>
          <div style={{color:"#fbbf24",fontSize:"11px",fontWeight:"700",marginBottom:"4px"}}>{PHASES[phaseIdx]}</div>
          <PhaseDots current={phaseIdx}/>
        </div>
      </div>

      <div style={{flex:1,overflowY:"auto",padding:"16px",display:"flex",flexDirection:"column",gap:"12px"}}>
        {messages.map((msg,i)=>(
          <div key={i} style={{display:"flex",justifyContent:msg.role==="user"?"flex-end":"flex-start",alignItems:"flex-end",gap:"8px"}}>
            {msg.role==="assistant"&&(
              <div style={{width:"30px",height:"30px",background:"#16a34a",borderRadius:"50%",
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:"13px",flexShrink:0}}>🧭</div>
            )}
            <div style={{maxWidth:"76%",padding:"10px 14px",fontSize:"14px",lineHeight:"1.55",
              borderRadius:msg.role==="user"?"18px 18px 4px 18px":"18px 18px 18px 4px",
              background:msg.role==="user"?"#16a34a":"white",
              color:msg.role==="user"?"white":"#1f2937",
              boxShadow:"0 1px 4px rgba(0,0,0,0.08)"}}>
              {msg.content}
            </div>
          </div>
        ))}
        {isTyping&&(
          <div style={{display:"flex",alignItems:"flex-end",gap:"8px"}}>
            <div style={{width:"30px",height:"30px",background:"#16a34a",borderRadius:"50%",
              display:"flex",alignItems:"center",justifyContent:"center",fontSize:"13px"}}>🧭</div>
            <div style={{background:"white",padding:"12px 16px",borderRadius:"18px 18px 18px 4px",boxShadow:"0 1px 4px rgba(0,0,0,0.08)"}}>
              <div style={{display:"flex",gap:"4px"}}>
                {[0,1,2].map(i=>(
                  <div key={i} style={{width:"8px",height:"8px",background:"#86efac",borderRadius:"50%",
                    animation:`dot 1.2s ${i*0.2}s infinite ease-in-out`}}/>
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef}/>
      </div>

      <div style={{padding:"12px 16px",background:"white",borderTop:"1px solid #e5e7eb",flexShrink:0}}>
        {awaitingConfirm ? (
          <div style={{display:"flex",gap:"10px"}}>
            <button onClick={onConfirmYes} style={{flex:1,padding:"12px",background:"#16a34a",color:"white",
              border:"none",borderRadius:"12px",fontSize:"15px",fontWeight:"700",cursor:"pointer"}}>
              🎉 Yes, build my report!
            </button>
            <button onClick={onConfirmNo} style={{flex:1,padding:"12px",background:"#f3f4f6",color:"#374151",
              border:"none",borderRadius:"12px",fontSize:"15px",fontWeight:"700",cursor:"pointer"}}>
              💬 Keep chatting
            </button>
          </div>
        ) : (
          <div style={{display:"flex",gap:"10px",alignItems:"center"}}>
            <input value={input} onChange={e=>setInput(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&onSend()}
              placeholder="Type your answer here..." disabled={isTyping}
              style={{flex:1,padding:"10px 16px",borderRadius:"24px",border:"1.5px solid #e5e7eb",
                fontSize:"14px",outline:"none",fontFamily:"inherit"}}/>
            <button onClick={onSend} disabled={isTyping||!input.trim()} style={{
              width:"44px",height:"44px",borderRadius:"50%",border:"none",cursor:"pointer",
              fontSize:"18px",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
              background:isTyping||!input.trim()?"#d1d5db":"#16a34a",color:"white"}}>➤</button>
          </div>
        )}
      </div>
      <style>{`@keyframes dot{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-6px)}}`}</style>
    </div>
  );
}

function LoadingScreen() {
  const [step,setStep]=useState(0);
  const steps=["Reading your answers...","Finding your strengths...","Matching your career families...","Writing your Direction Statement...","Almost done..."];
  useEffect(()=>{
    const t=setInterval(()=>setStep(s=>Math.min(s+1,steps.length-1)),2800);
    return()=>clearInterval(t);
  },[]);
  return (
    <div style={{height:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
      background:"linear-gradient(150deg,#14532d,#166534)",fontFamily:"system-ui,sans-serif",padding:"24px"}}>
      <div style={{fontSize:"56px",marginBottom:"24px",animation:"spin 3s linear infinite"}}>🧭</div>
      <h2 style={{color:"white",fontSize:"22px",fontWeight:"800",margin:"0 0 8px",textAlign:"center"}}>
        Building Your Career Compass Report
      </h2>
      <p style={{color:"#86efac",fontSize:"14px",margin:"0 0 32px",textAlign:"center"}}>This takes about 15 seconds…</p>
      <div style={{width:"100%",maxWidth:"320px",background:"rgba(255,255,255,0.08)",borderRadius:"14px",padding:"20px"}}>
        {steps.map((s,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:"10px",
            marginBottom:i<steps.length-1?"12px":0,opacity:i<=step?1:0.3,transition:"opacity 0.6s"}}>
            <div style={{width:"22px",height:"22px",borderRadius:"50%",flexShrink:0,
              background:i<=step?"#fbbf24":"rgba(255,255,255,0.15)",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:"11px",fontWeight:"700",color:i<=step?"#14532d":"white"}}>
              {i<=step?"✓":""}
            </div>
            <span style={{color:i<=step?"#fef3c7":"#86efac",fontSize:"13px"}}>{s}</span>
          </div>
        ))}
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function Card({icon,title,accent="#16a34a",children}) {
  return (
    <div style={{background:"white",borderRadius:"16px",padding:"18px 18px 16px",
      marginBottom:"14px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)",borderTop:`3px solid ${accent}`}}>
      <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"14px"}}>
        <span style={{fontSize:"18px"}}>{icon}</span>
        <span style={{fontWeight:"700",fontSize:"15px",color:"#111"}}>{title}</span>
      </div>
      {children}
    </div>
  );
}

const bt={fontSize:"14px",lineHeight:"1.6",color:"#4b5563",margin:0};

function ReportScreen({report, student, onUpgrade}) {
  if(!report||report.error) return (
    <div style={{padding:"40px",textAlign:"center",fontFamily:"system-ui,sans-serif"}}>
      <div style={{fontSize:"40px",marginBottom:"16px"}}>😔</div>
      <p style={{color:"#374151"}}>Something went wrong generating the report. Please contact Career GPS Africa at careergps.africa</p>
    </div>
  );
  const accs=["#16a34a","#f59e0b","#7c3aed"];
  const nums=["#dcfce7","#fef3c7","#ede9fe"];
  const ntxt=["#16a34a","#d97706","#7c3aed"];
  return (
    <div style={{minHeight:"100vh",background:"#f9fafb",fontFamily:"system-ui,sans-serif"}}>
      <div style={{background:"linear-gradient(150deg,#14532d,#166534)",padding:"24px 20px 36px"}}>
        <div style={{maxWidth:"580px",margin:"0 auto"}}>
          <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"18px"}}>
            <div style={{fontSize:"26px"}}>🧭</div>
            <div>
              <div style={{color:"white",fontWeight:"800",fontSize:"16px"}}>Career GPS Africa</div>
              <div style={{color:"#86efac",fontSize:"11px"}}>Career Compass Report</div>
            </div>
          </div>
          <h1 style={{color:"white",fontSize:"28px",fontWeight:"800",margin:"0 0 4px"}}>{student.name}</h1>
          <p style={{color:"#bbf7d0",fontSize:"13px",margin:"0 0 18px"}}>{student.grade} · {student.school} · {student.country}</p>
          <div style={{display:"inline-flex",alignItems:"center",gap:"10px",
            background:"rgba(255,255,255,0.12)",borderRadius:"28px",padding:"10px 18px"}}>
            <div style={{width:"40px",height:"40px",background:"#fbbf24",borderRadius:"50%",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontWeight:"900",color:"#92400e",fontSize:"18px"}}>{String(report.score??"-")}</div>
            <div>
              <div style={{color:"white",fontWeight:"700",fontSize:"14px"}}>{report.scoreLabel}</div>
              <div style={{color:"#86efac",fontSize:"11px"}}>Career Clarity Score /10</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{maxWidth:"580px",margin:"0 auto",padding:"20px 16px"}}>
        <Card icon="⚡" title={`Your Archetype: ${report.archetype}`} accent="#f59e0b">
          <p style={bt}>{report.archetypeDesc}</p>
          <div style={{display:"flex",flexWrap:"wrap",gap:"8px",marginTop:"12px"}}>
            {report.strengths?.map((s,i)=>(
              <div key={i} style={{background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:"8px",padding:"6px 12px"}}>
                <div style={{fontWeight:"700",fontSize:"12px",color:"#15803d"}}>{s.name}</div>
                <div style={{fontSize:"11px",color:"#6b7280"}}>{s.evidence}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card icon="🎯" title="Your Values Profile" accent="#8b5cf6">
          <p style={bt}>{report.values}</p>
        </Card>

        <Card icon="🗺️" title="Your Three Career Families" accent="#16a34a">
          <p style={{fontSize:"13px",color:"#6b7280",marginTop:0,marginBottom:"16px"}}>
            Three directions that match your unique profile — including careers most students have never heard of.
          </p>
          {report.careerFamilies?.map((fam,i)=>(
            <div key={i} style={{marginBottom:i<2?"18px":0,paddingBottom:i<2?"18px":0,
              borderBottom:i<2?"1px solid #f3f4f6":"none"}}>
              <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"6px"}}>
                <div style={{width:"26px",height:"26px",background:nums[i],borderRadius:"50%",
                  display:"flex",alignItems:"center",justifyContent:"center",
                  fontWeight:"800",fontSize:"12px",color:ntxt[i],flexShrink:0}}>{i+1}</div>
                <div style={{fontWeight:"700",fontSize:"15px",color:"#111"}}>{fam.name}</div>
              </div>
              <p style={{...bt,marginBottom:"10px"}}>{fam.fit}</p>
              <div style={{display:"flex",flexWrap:"wrap",gap:"6px",marginBottom:"10px"}}>
                {fam.careers?.map((c,j)=>(
                  <span key={j} style={{background:"#f9fafb",border:"1px solid #e5e7eb",
                    borderRadius:"6px",padding:"4px 10px",fontSize:"12px",color:"#374151"}}>{c}</span>
                ))}
              </div>
              <div style={{background:"#f0fdf4",borderLeft:"3px solid #16a34a",
                padding:"8px 12px",borderRadius:"0 8px 8px 0",fontSize:"12px",color:"#374151",lineHeight:"1.5"}}>
                🌍 <strong>African Opportunity:</strong> {fam.africa}
              </div>
            </div>
          ))}
        </Card>

        <Card icon="✍️" title="Your Career Direction Statement" accent="#f59e0b">
          <div style={{background:"linear-gradient(135deg,#f0fdf4,#dcfce7)",borderRadius:"12px",
            padding:"16px",borderLeft:"4px solid #16a34a"}}>
            <p style={{fontSize:"14px",lineHeight:"1.75",color:"#14532d",fontStyle:"italic",margin:"0 0 10px",fontWeight:"500"}}>
              "{report.statement}"
            </p>
            <p style={{margin:0,fontSize:"12px",color:"#16a34a",fontWeight:"700"}}>— {student.name}</p>
          </div>
        </Card>

        <Card icon="🚀" title="Your Next 30 Days" accent="#3b82f6">
          {report.actions?.map((a,i)=>(
            <div key={i} style={{display:"flex",gap:"12px",marginBottom:i<2?"14px":0,alignItems:"flex-start"}}>
              <div style={{width:"38px",height:"38px",background:nums[i],borderRadius:"10px",
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:"18px",flexShrink:0}}>
                {["👤","📺","🔨"][i]}
              </div>
              <div>
                <div style={{fontWeight:"700",fontSize:"13px",color:"#374151",marginBottom:"2px"}}>{a.type}</div>
                <div style={{fontSize:"13px",color:"#6b7280",lineHeight:"1.5"}}>{a.detail}</div>
              </div>
            </div>
          ))}
        </Card>

        <Card icon="📊" title="Clarity & Readiness" accent="#10b981">
          <div style={{display:"flex",gap:"14px",alignItems:"center"}}>
            <div style={{textAlign:"center",flexShrink:0}}>
              <div style={{fontSize:"46px",fontWeight:"900",color:"#16a34a",lineHeight:1}}>{String(report.score??"-")}</div>
              <div style={{fontSize:"11px",color:"#9ca3af"}}>/ 10</div>
            </div>
            <div>
              <div style={{fontWeight:"700",color:"#111",marginBottom:"4px"}}>{report.scoreLabel}</div>
              <p style={bt}>{report.scoreNote}</p>
            </div>
          </div>
        </Card>

        <Card icon="💬" title="A Note From Your Career Navigator" accent="#16a34a">
          <p style={{...bt,fontStyle:"italic"}}>{report.counselorNote}</p>
        </Card>

        <div style={{background:"linear-gradient(150deg,#14532d,#166534)",borderRadius:"16px",
          padding:"22px",marginBottom:"20px",boxShadow:"0 8px 24px rgba(20,83,45,0.35)"}}>
          <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"14px"}}>
            <span style={{fontSize:"22px"}}>👨‍👩‍👧</span>
            <span style={{color:"white",fontWeight:"700",fontSize:"16px"}}>Dear Parent / Guardian</span>
          </div>
          <p style={{color:"#bbf7d0",fontSize:"14px",lineHeight:"1.65",margin:"0 0 18px"}}>{report.parentNote}</p>
         <a href="https://calendly.com/careergpsafrica/30min" target="_blank"
  style={{display:"block",width:"100%",padding:"14px",background:"#fbbf24",border:"none",borderRadius:"12px",fontWeight:"800",fontSize:"15px",color:"#14532d",cursor:"pointer",textAlign:"center",boxSizing:"border-box",textDecoration:"none"}}>
  📞 Book a Free Career Navigation Call — Get a Clear Plan: Right Career, Right Subjects, Right University →
</a>
        </div>

                  <p style={{textAlign:"center",fontSize:"12px",color:"#9ca3af",marginBottom:"32px"}}>
          Career GPS Africa · careergps.africa · Serving Africa's Next Generation
        </p>
      </div>
    </div>
  );
}

export default function App() {
  const [screen,setScreen]=useState("landing");
  const [student,setStudent]=useState({parentName:"",name:"",age:"",school:"",grade:"",country:"",email:"",whatsapp:""});
  const [messages,setMessages]=useState([]);
  const [input,setInput]=useState("");
  const [isTyping,setIsTyping]=useState(false);
  const [report,setReport]=useState(null);
  const [errors,setErrors]=useState({});
  const [startLoading,setStartLoading]=useState(false);
  const [exchangeCount,setExchangeCount]=useState(0);
  const [awaitingConfirm,setAwaitingConfirm]=useState(false);
  const bottomRef=useRef(null);
useEffect(()=>{
  const params = new URLSearchParams(window.location.search);
  const path = window.location.pathname;
  if(path === "/payment/success") {
    setScreen("payment_success");
  } else if(params.get("checkout")==="true") {
    const urlName = params.get("name") || "";
    const urlEmail = params.get("email") || "";
    const urlPhone = params.get("phone") || "";
    const urlStudent = params.get("student") || "";
    if(urlEmail || urlName) {
      setStudent(s=>({...s,
        parentName: urlName,
        email: urlEmail,
        whatsapp: urlPhone,
        name: urlStudent
      }));
    }
    setScreen("checkout");
  }
},[]);
  useEffect(()=>{bottomRef.current?.scrollIntoView({behavior:"smooth"})},[messages,isTyping]);

  const goAdmin=()=>setScreen("admin_login");

  const validate=()=>{
    const e={};
    if(!student.parentName.trim()) e.parentName="Required";
    if(!student.name.trim()) e.name="Required";
    if(!student.age||+student.age<12||+student.age>20) e.age="Enter age 12–20";
    if(!student.grade) e.grade="Select a grade";
    if(!student.country) e.country="Select country";
    if(!/\S+@\S+\.\S+/.test(student.email)) e.email="Enter a valid email";
    if(!student.whatsapp.trim()) e.whatsapp="Required";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const callAI = async (msgs) => {
  const initCtx = `Student: ${student.name}, age ${student.age}, ${student.grade} at ${student.school} in ${student.country}. Begin now — greet them warmly by name and ask your first warm-up question.`;

  const isFirstCall = msgs.length === 0;
  const messageHistory = isFirstCall
    ? [{ role: "user", content: initCtx }]
    : msgs.map(m => ({ role: m.role, content: m.content }));

  for (let attempt = 0; attempt < 4; attempt++) {
    const r = await fetch("/api/claude", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 1000,
        system: COUNSELOR_SYS,
        messages: messageHistory
      })
    });
    const d = await r.json();
    if (d.type === "error" || d.error?.type === "overloaded_error") {
      await new Promise(res => setTimeout(res, 1000 * Math.pow(2, attempt)));
      continue;
    }
    return d.content[0].text;
  }
  throw new Error("API overloaded after retries");
};

  const startSession=async()=>{
    if(!validate()) return;
    setStartLoading(true);
    try {
      const txt=await callAI([]);
      setMessages([{role:"assistant",content:txt.replace("[SESSION_COMPLETE]","").replace("[ASK_READY]","").trim()}]);
    } catch {
      setMessages([{role:"assistant",content:`Hey ${student.name}! Welcome — so glad you're here. We're not making any big decisions today. We're just going on a little treasure hunt to find clues about what makes you tick. So let's start easy: if school was cancelled for a whole week, what would you actually do all day?`}]);
    }
    setStartLoading(false);
    setScreen("chat");
  };

  const sendMessage=async()=>{
    if(!input.trim()||isTyping) return;
    const userMsg={role:"user",content:input.trim()};
    const updated=[...messages,userMsg];
    setMessages(updated);
    setInput("");
    setIsTyping(true);
    setExchangeCount(c=>c+1);
    try {
      const txt=await callAI(updated);
      const done=txt.includes("[SESSION_COMPLETE]");
      const askReady=txt.includes("[ASK_READY]");
      const clean=txt.replace("[SESSION_COMPLETE]","").replace("[ASK_READY]","").trim();
      const final=[...updated,{role:"assistant",content:clean}];
      setMessages(final);
      if(askReady) setAwaitingConfirm(true);
      else if(done) setTimeout(()=>generateReport(final),2000);
    } catch(err) {
      setMessages(prev=>[...prev,{role:"assistant",content:`Sorry ${student.name}, I'm having a small problem right now. Please wait 30 seconds and try again — I haven't lost your conversation! 🙏`}]);
      fetch('/api/alert', {
    method: 'POST',
    headers: {'Content-Type':'application/json'},
    body: JSON.stringify({
      message: `Bot failed for ${student.name} at ${student.school}`,
      error: err?.message || 'Unknown error',
      time: new Date().toISOString()
      })
  });
    }
    setIsTyping(false);
  };

  const onConfirmYes=()=>{
    setAwaitingConfirm(false);
    const confirmMsg={role:"user",content:"Yes, go ahead and build my report!"};
    const updated=[...messages,confirmMsg];
    setMessages(updated);
    setTimeout(()=>generateReport(updated),800);
  };

  const onConfirmNo=async()=>{
    setAwaitingConfirm(false);
    const keepMsg={role:"user",content:"Let's keep chatting a bit more."};
    const updated=[...messages,keepMsg];
    setMessages(updated);
    setIsTyping(true);
    try {
      const txt=await callAI(updated);
      const clean=txt.replace("[ASK_READY]","").replace("[SESSION_COMPLETE]","").trim();
      setMessages([...updated,{role:"assistant",content:clean}]);
    } catch {}
    setIsTyping(false);
  };

  const generateReport=async(conv)=>{
    setScreen("loading_report");
    const transcript=conv.map(m=>`${m.role==="user"?student.name:"Nav"}: ${m.content}`).join("\n");
    let insightInput=transcript;
    try {
      const s=await fetch("/api/claude",{method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:400,
          system:SUMMARY_SYS,messages:[{role:"user",content:`Transcript:\n${transcript}`}]})});
      const sd=await s.json();
      insightInput=sd.content[0].text.trim();
    } catch {}
    try {
      const r=await fetch("/api/claude",{method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:1200,
          system:REPORT_SYS,
          messages:[{role:"user",content:`Student: ${student.name} | Age: ${student.age} | ${student.grade} | ${student.school} | ${student.country}\n\nInsights: ${insightInput}\n\nGenerate JSON now. Start with {`}]})});
      const d=await r.json();
      let raw=d.content[0].text.trim();
      const start=raw.indexOf("{");
      const end=raw.lastIndexOf("}");
      if(start!==-1&&end!==-1) raw=raw.slice(start,end+1);
      const parsed=JSON.parse(raw);
      setReport(parsed);
await fetch('/api/savelead', {
  method: 'POST',
  headers: {'Content-Type': 'application/json'},
  body: JSON.stringify({
    id: `lead_${Date.now()}`,
    completed_at: new Date().toISOString(),
    parent_name: student.parentName,
    student_name: student.name,
    age: student.age,
    grade: student.grade,
    school: student.school,
    country: student.country,
    email: student.email,
    whatsapp: student.whatsapp,
    archetype: parsed.archetype,
    score: parsed.score,
    score_label: parsed.scoreLabel,
    career_families: parsed.careerFamilies?.map(f=>f.name).join(', '),
    career_families_full: JSON.stringify(parsed.careerFamilies),
    statement: parsed.statement,
    parent_note: parsed.parentNote,
    status: 'New Lead'
  })
});
      
// ADD FROM HERE ↓
await fetch('/api/addlead', {
  method: 'POST',
  headers: {'Content-Type': 'application/json'},
  body: JSON.stringify({
    parentName: student.parentName,
    email: student.email,
    whatsapp: student.whatsapp,
    studentName: student.name,
    archetype: parsed.archetype,
    score: parsed.score,
    scoreLabel: parsed.scoreLabel,
    careerFamilies: parsed.careerFamilies?.map(f => f.name),
    statement: parsed.statement
  })
});
// ADD TO HERE ↑
    } catch { setReport({error:true}); }
    setScreen("report");
  };

  if(screen==="admin_login") return <AdminLogin onSuccess={()=>setScreen("admin")} onBack={()=>setScreen("landing")}/>;
  if(screen==="landing") return <LandingScreen student={student} setStudent={setStudent} errors={errors} onStart={startSession} loading={startLoading} onAdmin={goAdmin}/>;
  if(screen==="chat") return <ChatScreen messages={messages} input={input} setInput={setInput} onSend={sendMessage} isTyping={isTyping} student={student} messagesEndRef={bottomRef} exchangeCount={exchangeCount} awaitingConfirm={awaitingConfirm} onConfirmYes={onConfirmYes} onConfirmNo={onConfirmNo}/>;
  if(screen==="loading_report") return <LoadingScreen/>;
  if(screen==="admin") return <AdminScreen onBack={()=>setScreen("landing")}/>;
  if(screen==="checkout") return <PaystackCheckout parentEmail={student.email} parentName={student.parentName} studentName={student.name} phone={student.whatsapp}/>;
if(screen==="payment_success") return <PaymentSuccess />;
return <ReportScreen report={report} student={student} onUpgrade={()=>setScreen("checkout")}/>;
}
